// ============================================================================
// API CLIENT ÚNICO — capa de acceso a la API para TODO el frontend
// ============================================================================
// Unifica las tres capas históricas (ApiClient de AuthContext, el clon de
// TaskStore y los ~60 fetch crudos). Un solo sitio para: base URL por tenant,
// token Bearer, interceptor de sesión caducada y manejo de errores.
//
// INVARIANTES (no romper — de ellas depende el multi-tenant y la personalización):
//  - La base URL se deriva EN CADA LLAMADA de appConfig.getApiUrl() (que sale del
//    subdominio). NUNCA se captura una vez: eso rompería el aislamiento por tenant.
//  - El interceptor 401/403 cierra sesión SOLO si se envió token (si la petición
//    salió sin token —carrera de arranque— un 401 no es sesión caducada).
//  - NO toca get_public_config ni el single-flight window.__PUBLIC_CONFIG_FETCH__:
//    ese lo gestionan appConfig y clientConfigService.
//
// No importa AuthContext (evita ciclo): el manejo de sesión caducada se inyecta
// con registerUnauthorizedHandler().
// ============================================================================

import appConfig from '../config/appConfig';

// Handler de sesión caducada inyectado por AuthContext (evita el ciclo de imports).
let unauthorizedHandler = null;

/**
 * AuthContext registra aquí qué hacer cuando un 401/403 con token válido indica
 * sesión caducada (limpiar sesión, dispatch SESSION_EXPIRED, recargar).
 */
export function registerUnauthorizedHandler(fn) {
  unauthorizedHandler = fn;
}

/** Error de API con el status HTTP, para que los consumidores puedan discriminar. */
export class HttpError extends Error {
  constructor(message, status, payload) {
    super(message);
    this.name = 'HttpError';
    this.status = status;
    this.payload = payload;
  }
}

function getToken() {
  try {
    return localStorage.getItem('token');
  } catch (e) {
    return null;
  }
}

/**
 * Núcleo: hace la petición y devuelve el ENVELOPE JSON completo
 * ({status, message, data, timestamp}), igual que el ApiClient histórico —
 * para no romper a sus consumidores.
 *
 * opts:
 *   method, headers, body (ya serializado), signal   — estándar de fetch
 *   token      — override del token (si no, localStorage)
 *   skipAuth   — no enviar Authorization (endpoints públicos)
 *   raw        — devolver el objeto Response sin parsear (binarios/streaming)
 *   isForm     — el body es FormData: no fijar Content-Type (boundary automático)
 */
async function request(endpoint, opts = {}) {
  // Base URL SIEMPRE por llamada (tenant implícito por subdominio).
  const url = `${appConfig.getApiUrl()}?endpoint=${endpoint}`;

  const headers = { Accept: 'application/json', ...opts.headers };
  if (!opts.isForm && opts.body != null && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const token = opts.token !== undefined ? opts.token : getToken();
  if (token && !opts.skipAuth) {
    headers.Authorization = `Bearer ${token}`;
  }

  const fetchConfig = {
    method: opts.method || 'GET',
    headers,
  };
  if (opts.body != null) fetchConfig.body = opts.body;
  if (opts.signal) fetchConfig.signal = opts.signal;

  const response = await fetch(url, fetchConfig);

  // Interceptor de sesión caducada: SOLO si la petición llevaba token.
  if ((response.status === 401 || response.status === 403) && token && !opts.skipAuth) {
    if (unauthorizedHandler) unauthorizedHandler(response.status, endpoint);
    throw new HttpError('SESSION_EXPIRED', response.status);
  }

  if (opts.raw) return response;

  if (!response.ok) {
    let message = `HTTP ${response.status}: ${response.statusText}`;
    let payload = null;
    try {
      payload = await response.json();
      if (payload && payload.message) message = payload.message;
    } catch (e) { /* respuesta sin JSON */ }
    throw new HttpError(message, response.status, payload);
  }

  return response.json();
}

// --- Conveniencias: devuelven el envelope completo (compat con el ApiClient histórico) ---

function buildQuery(params) {
  if (!params) return '';
  const usp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null) usp.append(k, v);
  });
  const s = usp.toString();
  return s ? `&${s}` : '';
}

const apiClient = {
  request,
  registerUnauthorizedHandler,
  HttpError,

  get: (endpoint, params, opts = {}) =>
    request(`${endpoint}${buildQuery(params)}`, { ...opts, method: 'GET' }),

  post: (endpoint, body, opts = {}) =>
    request(endpoint, { ...opts, method: 'POST', body: body != null ? JSON.stringify(body) : undefined }),

  put: (endpoint, body, opts = {}) =>
    request(endpoint, { ...opts, method: 'PUT', body: body != null ? JSON.stringify(body) : undefined }),

  del: (endpoint, opts = {}) =>
    request(endpoint, { ...opts, method: 'DELETE' }),

  // Subida multipart: NO fija Content-Type (el navegador pone el boundary).
  upload: (endpoint, formData, opts = {}) =>
    request(endpoint, { ...opts, method: opts.method || 'POST', body: formData, isForm: true }),
};

export default apiClient;
